import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Avatar from '~/components/Avatar'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import List from '~/components/List'
import ListCollapse from '~/components/ListCollapse'
import ListItemAvatar from '~/components/ListItemAvatar'
import ListItemButton from '~/components/ListItemButton'
import ListItemCheckbox from '~/components/ListItemCheckbox'
import ListItemIcon from '~/components/ListItemIcon'
import ListItemSecondaryAction from '~/components/ListItemSecondaryAction'
import ListItemSwitch from '~/components/ListItemSwitch'
import ListItemText from '~/components/ListItemText'
import ListSubheader from '~/components/ListSubheader'

const ListPage: Component = () => {
  const [nestedOpen, setNestedOpen] = createSignal(false)
  const [wifiEnabled, setWifiEnabled] = createSignal(true)

  return (
    <>
      <Title>List - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">List</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Sistema modular para la representación de secuencias de datos. Desde menús de navegación hasta controles de configuración complejos, List ofrece una arquitectura flexible basada en
            subcomponentes atómicos.
          </p>
        </div>

        <div class="space-y-12">
          {/* Navegación y Estructura */}
          <Card>
            <Card.Header
              title="Navegación Intuitiva"
              subtitle="Elementos interactivos con soporte para iconos y estados de selección."
              icon={<Icon icon="material-symbols-light:list" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="max-w-md mx-auto p-4 bg-surface rounded-2xl shadow-xl border border-border overflow-hidden">
                <List>
                  <ListSubheader>Explorador de Archivos</ListSubheader>
                  <ListItemButton selected>
                    <ListItemIcon>
                      <Icon icon="material-symbols-light:home-outline" />
                    </ListItemIcon>
                    <ListItemText primary="Inicio / Dashboard" />
                    <Badge color="primary" size="sm">
                      New
                    </Badge>
                  </ListItemButton>
                  <ListItemButton>
                    <ListItemIcon>
                      <Icon icon="material-symbols-light:folder-outline" />
                    </ListItemIcon>
                    <ListItemText primary="Proyectos Privados" />
                  </ListItemButton>

                  <ListItemButton onClick={() => setNestedOpen(!nestedOpen())}>
                    <ListItemIcon>
                      <Icon icon="material-symbols-light:cloud-outline" />
                    </ListItemIcon>
                    <ListItemText primary="Nube Corporativa" />
                    <ListItemSecondaryAction>
                      <Icon icon={nestedOpen() ? 'material-symbols-light:expand-less' : 'material-symbols-light:expand-more'} size={16} />
                    </ListItemSecondaryAction>
                  </ListItemButton>

                  <ListCollapse in={nestedOpen()}>
                    <List disablePadding>
                      <ListItemButton class="pl-12">
                        <ListItemText primary="Backup_2024.zip" secondary="1.2 GB" />
                      </ListItemButton>
                      <ListItemButton class="pl-12">
                        <ListItemText primary="Assets_Design.pkg" secondary="450 MB" />
                      </ListItemButton>
                    </List>
                  </ListCollapse>
                </List>
              </div>
            </Card.Body>
          </Card>

          {/* Controles y Ajustes */}
          <Card>
            <Card.Header
              title="Controles Dinámicos"
              subtitle="Inyecta checkboxes o switches directamente en la lista para gestión de configuraciones."
              icon={<Icon icon="material-symbols-light:settings-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="p-6 bg-surface-muted rounded-2xl border border-border">
                  <ListSubheader inset>Permisos de Usuario</ListSubheader>
                  <List>
                    <ListItemCheckbox value="write" label="Acceso de Escritura" checked />
                    <ListItemCheckbox value="billing" label="Gestión de Facturación" />
                    <ListItemCheckbox value="audit" label="Audit Logs" checked />
                  </List>
                </div>
                <div class="p-6 bg-surface-muted rounded-2xl border border-border">
                  <ListSubheader inset>Conectividad</ListSubheader>
                  <List>
                    <ListItemSwitch value="wifi" label="Wi-Fi Enterprise" checked={wifiEnabled()} onChange={setWifiEnabled} secondary />
                    <ListItemSwitch value="bluetooth" label="Bluetooth 5.3" checked={false} secondary />
                  </List>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Comunicación y Avatares */}
          <Card>
            <Card.Header
              title="Contactos y Actividad"
              subtitle="Uso de avatares y texto multinivel para feeds o listas de usuarios."
              icon={<Icon icon="material-symbols-light:group-outline" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="max-w-2xl mx-auto space-y-4">
                <List divided>
                  <ListItemButton>
                    <ListItemAvatar>
                      <Avatar src="https://i.pravatar.cc/150?u=1" alt="Carlos Valiente" />
                    </ListItemAvatar>
                    <ListItemText primary="Carlos Valiente" secondary="Hace 10 min · Conectado en PC" />
                    <ListItemSecondaryAction>
                      <Button variant="soft" size="sm">
                        Mensaje
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                  <ListItemButton>
                    <ListItemAvatar>
                      <Avatar src="https://i.pravatar.cc/150?u=2" alt="Admin Group" />
                    </ListItemAvatar>
                    <ListItemText primary="Admin Group" secondary="3 participantes" />
                    <ListItemSecondaryAction>
                      <Icon icon="material-symbols-light:star-outline" class="text-amber-400" size={18} />
                    </ListItemSecondaryAction>
                  </ListItemButton>
                </List>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Soporte técnico para el componente List y sus derivados."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad (List)</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">divided</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Dibuja bordes entre elementos.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">variant</td>
                      <td class="px-6 py-4 italic text-xs">'default' | 'dense' | 'grid'</td>
                      <td class="px-6 py-4 font-mono">'default'</td>
                      <td class="px-6 py-4 italic">Control de densidad y layout rejilla.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">disablePadding</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Remueve paddings internos laterales.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">selectable</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Activa gestión de estado para items hijos.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default ListPage
